import type { WidgetSDK as IWidgetSDK, WidgetSDKConfig } from './widget-sdk.types'
import type { EventHandler } from './event-emitter.types'
import type { DesignTokens } from './design-token-store'
import type { VersionInfo } from '../version'
import { EventEmitter } from './event-emitter'
import { VERSION, getVersionInfo } from '../version'
import { getDesignTokens as getTokens, registerEmitter, unregisterEmitter } from './design-token-store'

export class WidgetSDK implements IWidgetSDK {
  readonly shadowRoot: ShadowRoot
  private readonly widgetType: string
  private props: Record<string, unknown>
  private readonly emitter: EventEmitter
  private ready: boolean = false
  private readonly readyPromise: Promise<IWidgetSDK>
  private readyResolve!: (sdk: IWidgetSDK) => void

  constructor(config: WidgetSDKConfig) {
    this.shadowRoot = config.shadowRoot
    this.widgetType = config.widgetType
    this.props = { ...config.props }
    this.emitter = new EventEmitter()

    this.readyPromise = new Promise((resolve) => {
      this.readyResolve = resolve
    })

    registerEmitter(this.emitter)
  }

  /** @internal */
  _markReady(): void {
    if (!this.ready) {
      this.ready = true
      this.readyResolve(this)
    }
  }

  /** @internal */
  _destroy(): void {
    this.emitter.emit('destroy')
    unregisterEmitter(this.emitter)
    this.emitter.removeAllListeners()
  }

  whenReady(): Promise<IWidgetSDK> {
    return this.readyPromise
  }

  getProps<T extends Record<string, unknown> = Record<string, unknown>>(): T {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return { ...this.props } as T
  }

  setProps(newProps: Record<string, unknown>): void {
    this.props = { ...this.props, ...newProps }
    this.emitter.emit('propsChanged', this.props)
  }

  get root(): ShadowRoot {
    return this.shadowRoot
  }

  getContainer(): ShadowRoot {
    return this.shadowRoot
  }

  $<T extends Element = Element>(selector: string): T | null {
    return this.shadowRoot.querySelector<T>(selector)
  }

  $$<T extends Element = Element>(selector: string): T[] {
    return Array.from(this.shadowRoot.querySelectorAll<T>(selector))
  }

  // eslint-disable-next-line class-methods-use-this
  get document(): Document {
    return globalThis.document
  }

  // eslint-disable-next-line class-methods-use-this
  get version(): string {
    return VERSION
  }

  // eslint-disable-next-line class-methods-use-this
  getVersionInfo(): VersionInfo {
    return getVersionInfo()
  }

  on<T = unknown>(event: string, handler: EventHandler<T>): () => void {
    return this.emitter.on(event, handler)
  }

  off(event: string, handler: EventHandler): void {
    this.emitter.off(event, handler)
  }

  emit(event: string, data?: unknown): void {
    this.emitter.emit(event, data)
  }

  // eslint-disable-next-line class-methods-use-this
  getDesignTokens(): DesignTokens {
    return getTokens()
  }

  /** @internal */
  getWidgetType(): string {
    return this.widgetType
  }
}
